import React, { useState, useRef } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { GoogleMap, LoadScript, Marker, Autocomplete } from "@react-google-maps/api";
import { API } from "@/App";

function ClientProfile({ clientDetails }) {
  const [form, setForm] = useState({
    name: clientDetails?.name || "",
    email: clientDetails?.contact_email || clientDetails?.email || "",
    phone: clientDetails?.phone || "",
    address: clientDetails?.address || "",
    organization: clientDetails?.organization || "",
    password: "",
    active: clientDetails?.active !== undefined ? clientDetails.active : true,
    latitude: clientDetails?.latitude || null,
    longitude: clientDetails?.longitude || null,
  });
  const [loading, setLoading] = useState(false);
  const [autocomplete, setAutocomplete] = useState(null);
  // Google Maps API key (replace with your own or use env variable)
  const GOOGLE_MAPS_API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY || "YOUR_GOOGLE_MAPS_API_KEY";

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setForm((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? checked : value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      // Update client profile API
      const res = await fetch(`${API}/master/clients/${clientDetails.id}`, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${localStorage.getItem("token")}`,
        },
        body: JSON.stringify({
          name: form.name,
          email: form.email,
          phone: form.phone,
          address: form.address,
          organization: form.organization,
          password: form.password,
          active: form.active,
        }),
      });
      if (!res.ok) {
        throw new Error("Failed to update client profile");
      }
      toast.success("Client profile updated successfully!");
    } catch (err) {
      toast.error("Update failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="bg-white p-8 rounded-lg shadow">
      <h2 className="text-2xl font-bold text-[#1A3C34] mb-6">Client Profile</h2>
      <form onSubmit={handleSubmit} className="space-y-6">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="organization">Organization Name</Label>
            <Input
              id="organization"
              name="organization"
              value={form.organization}
              onChange={handleChange}
              className="mt-2"
              required
            />
          </div>
          <div>
            <Label htmlFor="email">Contact Email</Label>
            <Input
              id="email"
              name="email"
              type="email"
              value={form.email}
              onChange={handleChange}
              className="mt-2"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="address">Address</Label>
            <div className="relative">
              <LoadScript googleMapsApiKey={GOOGLE_MAPS_API_KEY} libraries={["places"]}>
                <Autocomplete
                  onLoad={auto => setAutocomplete(auto)}
                  onPlaceChanged={() => {
                    if (autocomplete) {
                      const place = autocomplete.getPlace();
                      setForm(prev => ({
                        ...prev,
                        address: place.formatted_address || place.name || "",
                        latitude: place.geometry?.location?.lat() || null,
                        longitude: place.geometry?.location?.lng() || null,
                      }));
                    }
                  }}
                >
                  <Input
                    id="address"
                    name="address"
                    value={form.address}
                    onChange={handleChange}
                    autoComplete="off"
                    className="mt-2"
                    required
                  />
                </Autocomplete>
                {/* Map preview */}
                {form.latitude && form.longitude && (
                  <div className="mt-4">
                    <GoogleMap
                      mapContainerStyle={{ width: "100%", height: "200px", borderRadius: "8px" }}
                      center={{ lat: form.latitude, lng: form.longitude }}
                      zoom={16}
                    >
                      <Marker position={{ lat: form.latitude, lng: form.longitude }} />
                    </GoogleMap>
                  </div>
                )}
              </LoadScript>
            </div>
          </div>
          <div>
            <Label htmlFor="phone">Phone Number</Label>
            <Input
              id="phone"
              name="phone"
              value={form.phone}
              onChange={handleChange}
              className="mt-2"
              required
            />
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div>
            <Label htmlFor="password">Password</Label>
            <Input
              id="password"
              name="password"
              type="password"
              value={form.password}
              onChange={handleChange}
              className="mt-2"
              placeholder="Leave blank to keep unchanged"
            />
          </div>
          <div className="flex items-center mt-6">
            <input
              id="active"
              name="active"
              type="checkbox"
              checked={form.active}
              onChange={handleChange}
              className="mr-2"
            />
            <Label htmlFor="active">Active</Label>
          </div>
        </div>

        <Button type="submit" disabled={loading} className="mt-4">
          {loading ? "Updating..." : "Update Profile"}
        </Button>
      </form>
    </div>
  );
}

export default ClientProfile;